import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styles from './styles.module.scss';

const DesktopView = ({ cards = [] }) => {
  const [activeImageId, setActiveImageId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  // Split features in half
  const splitFeatures = useMemo(() => {
    if (!cards || cards.length === 0) {
      return { leftFeatures: [], rightFeatures: [] };
    }
    const midPoint = Math.ceil(cards.length / 2);
    return {
      leftFeatures: cards.slice(0, midPoint),
      rightFeatures: cards.slice(midPoint),
    };
  }, [cards]);

  const { leftFeatures, rightFeatures } = splitFeatures;
  const hasLeftFeatures = leftFeatures && leftFeatures.length > 0;
  const hasRightFeatures = rightFeatures && rightFeatures.length > 0;

  // Append all images on mount
  useEffect(() => {
    if (cards && cards.length > 0) {
      setActiveImageId(cards[0]?.image?.id);
      cards.forEach((card) => {
        const imageDiv = document.getElementById(`img-box-${card?.image?.id}`);
        const image = document.getElementById(`img-${card?.image?.id}`);
        if (imageDiv && image) {
          imageDiv.innerHTML = '';
          imageDiv.appendChild(image.cloneNode(true));
        }
      });
    }
  }, [cards]);

  // Auto-rotation every 5 seconds
  useEffect(() => {
    if (!cards || cards.length === 0 || isPaused) {
      return undefined;
    }

    const interval = setInterval(() => {
      setActiveImageId((currentId) => {
        if (!currentId) return cards[0]?.image?.id;

        const currentIndex = cards.findIndex((card) => card?.image?.id === currentId);
        const nextIndex = (currentIndex + 1) % cards.length;
        return cards[nextIndex]?.image?.id;
      });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [cards, isPaused]);

  const handleFeatureHover = useCallback((feature) => {
    if (feature?.image?.id) {
      setIsPaused(true);
      setActiveImageId(feature.image.id);
    }
  }, []);

  const handleFeatureLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleFeatureFocus = useCallback((feature) => {
    if (feature?.image?.id) {
      setIsPaused(true);
      setActiveImageId(feature.image.id);
    }
  }, []);

  const handleFeatureBlur = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleFeatureClick = useCallback((feature) => {
    if (feature?.image?.id) {
      setActiveImageId(feature.image.id);
    }
  }, []);

  const renderFeature = useCallback(
    (feature, index) => {
      const isActive = activeImageId === feature.image.id;
      return (
        <button
          key={feature.id || index}
          type="button"
          className={cn(styles.feature, {
            [styles.active]: isActive,
          })}
          onMouseEnter={() => handleFeatureHover(feature)}
          onMouseLeave={handleFeatureLeave}
          onFocus={() => handleFeatureFocus(feature)}
          onBlur={handleFeatureBlur}
          onClick={() => handleFeatureClick(feature)}
          aria-label={feature.title}
        >
          {feature.title && <div className={styles.title}>{feature.title}</div>}
          {feature.description && (
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: feature.description }}
            />
          )}
        </button>
      );
    },
    [
      activeImageId,
      handleFeatureHover,
      handleFeatureLeave,
      handleFeatureFocus,
      handleFeatureBlur,
      handleFeatureClick,
    ]
  );

  return (
    <div className={styles.content}>
      {hasLeftFeatures && (
        <div className={styles.leftFeatures}>
          {leftFeatures.map((feature, index) => renderFeature(feature, index))}
        </div>
      )}
      <div className={styles.imageContainer}>
        {cards.map((card) => (
          <div
            key={`img-box-${card?.image?.id}`}
            className={cn(styles.imageBox, {
              [styles.active]: card?.image?.id === activeImageId,
            })}
            id={`img-box-${card?.image?.id}`}
          />
        ))}
      </div>
      {hasRightFeatures && (
        <div className={styles.rightFeatures}>
          {rightFeatures.map((feature, index) => renderFeature(feature, index))}
        </div>
      )}
    </div>
  );
};

DesktopView.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
    })
  ),
};


export default DesktopView;
